import { lazy, Suspense, useState, useCallback } from "react";
import Header from "@/components/Header";
import Hero from "@/components/Hero";
import CountdownBanner from "@/components/CountdownBanner";
import { SEO, OrganizationJsonLd } from "@/components/SEO";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import {
  FeaturedProductsSkeleton,
  CategoriesSkeleton,
  InstagramFeedSkeleton,
  NewsletterSkeleton,
} from "@/components/HomepageSkeletons";

// Lazy load all below-the-fold components to improve TTI
const FeaturedProducts = lazy(() => import("@/components/FeaturedProducts"));
const Categories = lazy(() => import("@/components/Categories"));
const InstagramFeed = lazy(() => import("@/components/InstagramFeed"));
const Newsletter = lazy(() => import("@/components/Newsletter"));
const Footer = lazy(() => import("@/components/Footer"));

const Index = () => {
  const [prefsOpen, setPrefsOpen] = useState(false);
  const [bannerVisible, setBannerVisible] = useState(false);
  const [bannerHeight, setBannerHeight] = useState(0);
  const handlePrefsChange = useCallback((open: boolean) => setPrefsOpen(open), []);
  const handleBannerVisibilityChange = useCallback((visible: boolean) => setBannerVisible(visible), []);
  const handleBannerHeightChange = useCallback((height: number) => setBannerHeight(height), []);

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Korean Inspired Minimal Streetwear"
        url="/"
        description="Union Collection — home of the Union Waistband, our signature internal-belt design. Korean-inspired minimal streetwear built on presence, not noise."
      />
      <OrganizationJsonLd />
      <Header
        onPrefsChange={handlePrefsChange}
        mobileTopOffset={bannerVisible ? bannerHeight : 0}
      />
      <div className={`fixed left-0 right-0 top-0 z-[51] transition-all duration-300 ease-out md:z-[39] ${
        prefsOpen ? "md:top-[calc(5rem+3rem)]" : "md:top-20"
      }`}>
        <CountdownBanner
          onVisibilityChange={handleBannerVisibilityChange}
          onHeightChange={handleBannerHeightChange}
        />
      </div>
      <div
        aria-hidden="true"
        className="md:hidden transition-all duration-300 ease-out"
        style={{ height: bannerVisible ? `${bannerHeight}px` : "0px" }}
      />
      <main>
        <ErrorBoundary>
          <Hero />
        </ErrorBoundary>
        <ErrorBoundary>
          <Suspense fallback={<FeaturedProductsSkeleton />}>
            <FeaturedProducts />
          </Suspense>
        </ErrorBoundary>
        <ErrorBoundary>
          <Suspense fallback={<CategoriesSkeleton />}>
            <Categories />
          </Suspense>
        </ErrorBoundary>
        <ErrorBoundary>
          <Suspense fallback={<InstagramFeedSkeleton />}>
            <InstagramFeed />
          </Suspense>
        </ErrorBoundary>
        <ErrorBoundary>
          <Suspense fallback={<NewsletterSkeleton />}>
            <Newsletter />
          </Suspense>
        </ErrorBoundary>
      </main>
      <Suspense fallback={null}>
        <Footer />
      </Suspense>
    </div>
  );
};

export default Index;
